import { captureErrorMessage } from './analytics/error-reporting';
import { ServerError } from '../types';

/**
 * Wrapper around fetch that throws an error for 5xx response status codes.
 *
 * @throws ServerErrorException if the response status code is 5xx.
 */
export const fetchApi = async (url: string, headers?: HeadersInit) => {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  if (response.status >= 500) {
    const data = await response.json();
    const message = `Failed to fetch ${url} [${response.status} status]`;
    const errorType = (data?.error as ServerError) || { error: 'internal_server_error' };
    void captureErrorMessage(message, data);
    throw new ServerErrorException(message, response.status, errorType);
  }

  return response;
};

export class ServerErrorException extends Error {
  constructor(public message: string, public status: number, public errorType: ServerError) {
    super(message);
    this.name = 'ServerErrorException';
  }
}

export class FetchTimeoutError extends Error {
  constructor(public originalError: unknown) {
    const message =
      originalError instanceof Error
        ? `FetchTimeoutError: ${originalError.message}`
        : 'FetchTimeoutError';
    super(message);
    this.name = 'FetchTimeoutError';
  }
}

/**
 * Calls fetchApi with automatic retries when `fetchApi` throws an error (eg. network errors)
 * Server errors (e.g. `ServerErrorException`) are rethrown immediately.
 *
 * @throws `FetchTimeoutError` after all retry attempts are exhausted.
 */
export async function fetchApiWithRetry(
  url: string,
  headers?: HeadersInit,
  delayMs = 1000,
  retries = 2
): Promise<Response> {
  try {
    return await fetchApi(url, headers);
  } catch (error) {
    if (error instanceof ServerErrorException) {
      throw error;
    }

    if (retries <= 0) {
      captureErrorMessage(`Unexpected error while loading AppState in Bifrost: ${error}`);
      throw new FetchTimeoutError(error);
    }

    await new Promise(resolve => setTimeout(resolve, delayMs));
    return fetchApiWithRetry(url, headers, delayMs * 2, retries - 1);
  }
}
